import * as React from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  Search,
} from 'lucide-react'
import { RequestCourseDialog } from './-components/RequestCourseDialog'
import { LearningImage } from './-components/LearningImage'
import { PageTitle } from '@/components/PageTitle'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useBrowseCourses } from '@/hooks/repositories/useLearningRepository'
import { useDebouncedValue } from '@/hooks/use-cases/useDebouncedValue'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/learning/browse')({
  component: RouteComponent,
})

/**
 * One row of filter pills.
 *
 * Hidden entirely when there is nothing to filter by — an empty "Technology"
 * row is a promise the catalogue is not keeping. Clicking the selected pill
 * clears it, so there is no separate reset to find.
 */
function TermFilter({
  label,
  options,
  selected,
  onSelect,
}: {
  label: string
  options: Array<{ id: string; name: string }>
  selected: string | null
  onSelect: (id: string | null) => void
}) {
  if (options.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-xs font-medium text-muted-foreground">
        {label}
      </span>
      <button
        type="button"
        onClick={() => onSelect(null)}
        className={cn(
          'rounded-full border px-3 py-1 text-xs transition-colors',
          selected === null
            ? 'border-primary bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-muted',
        )}
      >
        All
      </button>
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => onSelect(selected === option.id ? null : option.id)}
          className={cn(
            'rounded-full border px-3 py-1 text-xs transition-colors',
            selected === option.id
              ? 'border-primary bg-primary/10 text-primary'
              : 'text-muted-foreground hover:bg-muted',
          )}
        >
          {option.name}
        </button>
      ))}
    </div>
  )
}

/**
 * The full catalogue (sc-1245).
 *
 * Its own page rather than a section on the landing, because it has to scale:
 * a hundred courses is a browse problem — search, a category filter, and pages
 * — and the landing page is about what you owe, not what exists.
 *
 * Category filters come from the server and only include categories that have
 * a published course behind them, so it can never offer a filter that returns
 * nothing.
 */
function RouteComponent() {
  const [search, setSearch] = React.useState('')
  const [categoryId, setCategoryId] = React.useState<string | null>(null)
  const [technologyId, setTechnologyId] = React.useState<string | null>(null)
  const [page, setPage] = React.useState(1)

  const debouncedSearch = useDebouncedValue(search, 300)

  // Any change to the filters invalidates the page number — staying on page 4
  // of a new result set usually means an empty screen.
  React.useEffect(() => {
    setPage(1)
  }, [debouncedSearch, categoryId, technologyId])

  const { data, isPending } = useBrowseCourses({
    search: debouncedSearch || undefined,
    categoryId: categoryId ?? undefined,
    technologyId: technologyId ?? undefined,
    page,
  })

  const totalPages = data
    ? Math.max(1, Math.ceil(data.total / data.pageSize))
    : 1

  return (
    <div className="py-4">
      <PageTitle title="Learning — Browse" />

      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link to="/learning">
          <ArrowLeft className="mr-1 size-4" aria-hidden />
          Learning
        </Link>
      </Button>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
            Browse courses
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Everything published. Anyone can take a course, whether or not it
            has been assigned to them.
          </p>
        </div>
        <RequestCourseDialog />
      </div>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <div className="mb-4 space-y-3">
        <div className="relative max-w-md">
          <Search
            className="absolute top-2.5 left-2.5 size-4 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search courses"
            className="pl-8"
            aria-label="Search courses"
          />
        </div>

        <TermFilter
          label="Category"
          options={data?.categories ?? []}
          selected={categoryId}
          onSelect={setCategoryId}
        />
        <TermFilter
          label="Technology"
          options={data?.technologies ?? []}
          selected={technologyId}
          onSelect={setTechnologyId}
        />
      </div>

      {/* ── Results ────────────────────────────────────────────────────── */}
      {isPending ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : !data || data.courses.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <div className="rounded-full bg-muted p-3">
              <BookOpen className="size-6 text-muted-foreground" aria-hidden />
            </div>
            <div>
              <p className="text-sm font-medium">Nothing matches</p>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                Try a different search, or ask for the course you were looking
                for.
              </p>
            </div>
            <div className="mt-2">
              <RequestCourseDialog />
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.courses.map((course) => (
              <Link
                key={course.courseId}
                to="/learning/$courseSlug"
                params={{ courseSlug: course.courseSlug }}
                className="group flex flex-col overflow-hidden rounded-xl border bg-card transition-all hover:-translate-y-0.5 hover:shadow-md motion-reduce:hover:translate-y-0"
              >
                {/* Only where there is one. A placeholder band on every card
                    would make a catalogue of unillustrated courses look
                    broken rather than plain. */}
                {course.heroImageAssetId && (
                  <LearningImage
                    assetId={course.heroImageAssetId}
                    alt=""
                    className="h-28 w-full"
                  />
                )}

                <div className="flex flex-1 flex-col gap-3 p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="rounded-lg bg-muted p-2.5">
                      <BookOpen
                        className="size-5 text-muted-foreground"
                        aria-hidden
                      />
                    </div>
                    {course.assignmentStatus === 'COMPLETED' ? (
                      <Badge
                        variant="outline"
                        className="border-emerald-500/30 bg-emerald-500/10 text-[10px] text-emerald-700 dark:text-emerald-400"
                      >
                        <CheckCircle2 className="mr-1 size-2.5" aria-hidden />
                        Done
                      </Badge>
                    ) : course.assignmentStatus ? (
                      // Already on their list — saying so stops a course they are
                      // partway through presenting itself as something new.
                      <Badge variant="secondary" className="text-[10px]">
                        Assigned
                      </Badge>
                    ) : null}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold group-hover:underline">
                      {course.title}
                    </h3>
                    {course.summary && (
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                        {course.summary}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    {course.categories.map((name) => (
                      <Badge
                        key={name}
                        variant="outline"
                        className="text-[10px]"
                      >
                        {name}
                      </Badge>
                    ))}
                    {course.technologies.map((name) => (
                      <Badge
                        key={name}
                        variant="outline"
                        className="border-sky-500/30 bg-sky-500/10 text-[10px] text-sky-700 dark:text-sky-400"
                      >
                        {name}
                      </Badge>
                    ))}
                    <span>
                      {course.lessonCount}{' '}
                      {course.lessonCount === 1 ? 'lesson' : 'lessons'}
                    </span>
                    {course.estimatedMinutes != null && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="size-3" aria-hidden />
                        {course.estimatedMinutes} min
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Page {data.page} of {totalPages} · {data.total} courses
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => current - 1)}
                >
                  <ChevronLeft className="size-4" aria-hidden />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                  <ChevronRight className="size-4" aria-hidden />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
